import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getMe, updateProfile } from "../api/auth";
import { i18next, persistLanguage } from "../i18n/config";
import { isSupportedLanguage, type SupportedLanguage } from "../i18n/types";
import { useAuth } from "./useAuth";

export function useLanguage() {
	const { token } = useAuth();
	const { i18n } = useTranslation();
	const queryClient = useQueryClient();

	// Reuse the ["me"] query already used by ProfilePage — no extra network request
	const { data: user } = useQuery({
		queryKey: ["me"],
		queryFn: getMe,
		enabled: !!token,
	});

	// When server data arrives, sync i18next if it differs (e.g. user logged in on a different device)
	useEffect(() => {
		if (!user?.language) return;
		if (!isSupportedLanguage(user.language)) return;
		if (i18n.language === user.language) return;
		i18n.changeLanguage(user.language);
		persistLanguage(user.language);
	}, [user?.language, i18n]);

	const mutation = useMutation({
		mutationFn: (lang: SupportedLanguage) => updateProfile({ language: lang }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["me"] });
		},
	});

	const setLanguage = useCallback(
		(lang: SupportedLanguage) => {
			i18next.changeLanguage(lang);
			persistLanguage(lang);
			if (token) {
				mutation.mutate(lang);
			}
		},
		[token, mutation],
	);

	return {
		currentLanguage: (i18n.language ?? "en") as SupportedLanguage,
		setLanguage,
		isSyncing: mutation.isPending,
	};
}
